import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SessionState } from 'sip.js';
import { useBackendPhonePause } from '@/shared/hooks/useBackendPhonePause';
import { useSipCall } from '@/shared/providers/SipCallProvider';
import hangupToneSrc from '@/assets/softphone/freesound_community-mobile_phone_hanging_up-94525.mp3';
import { isSipAccountConfigured } from '@/services/sip/sipAccount.fromProfile';
import SoftphoneDialpad from './SoftphoneDialpad';
const HANGUP_TONE_VOLUME = 0.55;
const MAX_DIGITS = 64;
function sanitizeDialInput(raw) {
    return raw.replace(/[^\d+#*\s-]/g, '').slice(0, MAX_DIGITS);
}
const PHONE_ICON_PATH = 'M759.4 646.6c-47.3-47.3-93-59.7-129.2-35.2l-69.5 47c-22.2 15-51.9 12.1-70.6-7L373.6 534.9c-19-18.9-21.9-48.6-7-70.8l46.9-69.5c24.5-36.2 12.1-81.9-35.2-129.2l-57.7-57.7c-34-34-89.4-34-123.4 0l-34.7 34.7c-42.6 42.6-59.4 104.8-45.2 166.7 22.1 96.6 78.8 193.6 164.1 278.9s182.3 142 278.9 164.1c61.9 14.2 124.1-2.6 166.7-45.2l34.7-34.7c34-34 34-89.4 0-123.4l-2.3-2.2z';
/** Matches {@link CallIcon} hangup SVG in layout toolbar. */
const HANGUP_ICON_PATH = 'M512 214c-176.8 0-340.7 56.3-474.3 152.1C14.2 382.9 0 410.1 0 439v98.2c0 39.8 32.2 72 72 72h146.8c34.1 0 63.6-23.9 70.5-57.3l21.3-103.2c62.7-18.7 130.4-28.7 201.4-28.7s138.7 10 201.4 28.7l21.3 103.2c6.9 33.4 36.4 57.3 70.5 57.3H952c39.8 0 72-32.2 72-72V439c0-28.9-14.2-56.1-37.7-72.9C852.7 270.3 688.8 214 512 214zM338.7 718.7c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128 128 128c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-128-128 128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-128 128-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128-128 128z';
function pad2(n) {
    return String(Math.max(0, n)).padStart(2, '0');
}
function formatMmSs(totalSeconds) {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${pad2(m)}:${pad2(s)}`;
}
function sipRegistrationHint(sipStatus, sipError) {
    if (sipError) {
        if (sipStatus === 'error') {
            return { line: `SIP error: ${sipError}`, tone: 'warn' };
        }
        /** Message set while still negotiating (e.g. REGISTER pending hint). */
        return { line: sipError, tone: 'warn' };
    }
    switch (sipStatus) {
        case 'registered':
            return { line: 'Online', tone: 'ok' };
        case 'connected':
            return { line: 'Connected (registering…)', tone: 'muted' };
        case 'connecting':
            return { line: 'Connecting…', tone: 'muted' };
        case 'error':
            return { line: 'Offline', tone: 'warn' };
        default:
            return { line: 'SIP idle', tone: 'muted' };
    }
}
function sipReadyForOutboundDial(sipStatus) {
    return sipStatus === 'registered' || sipStatus === 'connected';
}
function sipCallPhaseLabel(ui) {
    const outboundLineBusy = ui.direction === 'outbound' &&
        ui.outboundPhase === 'line-busy' &&
        (ui.sipState === SessionState.Initial ||
            ui.sipState === SessionState.Establishing ||
            ui.sipState === SessionState.Terminating);
    if (outboundLineBusy) {
        return 'Ringing…';
    }
    switch (ui.sipState) {
        case SessionState.Initial:
            return ui.direction === 'outbound' ? 'Calling…' : 'Incoming…';
        case SessionState.Establishing:
            return ui.direction === 'outbound' ? 'Ringing…' : 'Connecting…';
        case SessionState.Established:
            return 'In call';
        case SessionState.Terminating:
            return 'Ending call…';
        case SessionState.Terminated:
            return '';
        default:
            return '';
    }
}
function sipCallPhaseTone(ui) {
    const outboundLineBusy = ui.direction === 'outbound' &&
        ui.outboundPhase === 'line-busy' &&
        (ui.sipState === SessionState.Initial ||
            ui.sipState === SessionState.Establishing ||
            ui.sipState === SessionState.Terminating);
    if (outboundLineBusy) {
        return '--muted';
    }
    if (ui.sipState === SessionState.Established)
        return '--active';
    if (ui.sipState === SessionState.Terminating || ui.sipState === SessionState.Terminated)
        return '--muted';
    if (ui.sipState === SessionState.Establishing || ui.sipState === SessionState.Initial)
        return '--ringing';
    return '--muted';
}
export default function SoftphoneModal({ visible, onHide, onDial }) {
    const { sipStatus, sipError, isOnBreak, dial, sendDtmf, account, conversationUi, hangupActiveOrDeclineIncoming, } = useSipCall();
    const { isOnBackendAcw, acwRemainingSeconds, acwElapsedSeconds } = useBackendPhonePause();
    const panelRef = useRef(null);
    const headerRef = useRef(null);
    const hangupAudioRef = useRef(null);
    const dragRef = useRef({
        dragging: false,
        startX: 0,
        startY: 0,
        originLeft: 0,
        originTop: 0,
    });
    const [enteredNumber, setEnteredNumber] = useState('');
    /** When set, panel uses explicit left/top (after drag or first grab); null = CSS top/right defaults */
    const [panelPos, setPanelPos] = useState(null);
    const [dialFeedback, setDialFeedback] = useState(null);
    const playHangupTone = useCallback(() => {
        let a = hangupAudioRef.current;
        if (!a) {
            a = new Audio(hangupToneSrc);
            hangupAudioRef.current = a;
        }
        a.loop = false;
        a.volume = HANGUP_TONE_VOLUME;
        a.currentTime = 0;
        void a.play().catch(() => undefined);
    }, []);
    /** Re-render ~4×/s so timers advance without waiting for SIP events */
    const [timerTick, setTimerTick] = useState(0);
    useEffect(() => {
        if (!conversationUi)
            return;
        const id = window.setInterval(() => setTimerTick((n) => n + 1), 250);
        return () => window.clearInterval(id);
    }, [conversationUi]);
    useEffect(() => {
        if (!visible) {
            setEnteredNumber('');
            setPanelPos(null);
            setDialFeedback(null);
        }
    }, [visible]);
    const inEstablishedCall = conversationUi?.sipState === SessionState.Established;
    const appendDigit = useCallback((value) => {
        if (inEstablishedCall) {
            setEnteredNumber((prev) => (prev + value).slice(-MAX_DIGITS));
            if (/^[0-9A-D#*,]$/.test(value)) {
                void sendDtmf(value).catch((e) => {
                    const detail = e instanceof Error ? e.message : String(e);
                    setDialFeedback(detail);
                });
            }
            return;
        }
        setEnteredNumber((prev) => (prev + value).slice(-MAX_DIGITS));
    }, [inEstablishedCall, sendDtmf]);
    const clearNumber = useCallback(() => {
        setEnteredNumber('');
    }, []);
    const removeLast = useCallback(() => {
        setEnteredNumber((prev) => prev.slice(0, -1));
    }, []);
    /** Lock pad while ringing/connecting; enable during established call for IVR DTMF. */
    const dialpadLocked = conversationUi !== null && !inEstablishedCall;
    const dialpadMode = inEstablishedCall ? 'dtmf' : 'dial';
    useEffect(() => {
        if (!visible)
            return;
        const onKeyDown = (event) => {
            const target = event.target;
            if (target &&
                (target.tagName === 'INPUT' ||
                    target.tagName === 'TEXTAREA' ||
                    target.isContentEditable) &&
                !target.closest('.softphone-modal')) {
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                onHide();
                return;
            }
            if (dialpadLocked) {
                if ('0123456789+#'.includes(event.key) ||
                    event.key === 'Backspace' ||
                    event.key.toLowerCase() === 'c') {
                    event.preventDefault();
                }
                return;
            }
            const allowed = '0123456789+#';
            if (allowed.includes(event.key)) {
                event.preventDefault();
                appendDigit(event.key);
                return;
            }
            if (event.key === 'Backspace') {
                event.preventDefault();
                removeLast();
                return;
            }
            if (event.key.toLowerCase() === 'c') {
                event.preventDefault();
                clearNumber();
                return;
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [visible, appendDigit, removeLast, clearNumber, onHide, dialpadLocked]);
    const endDrag = useCallback(() => {
        dragRef.current.dragging = false;
        document.body.style.userSelect = '';
    }, []);
    const onHeaderMouseDown = useCallback((event) => {
        if (event.target.closest('button'))
            return;
        const phone = panelRef.current;
        const header = headerRef.current;
        if (!phone || !header)
            return;
        event.preventDefault();
        const rect = phone.getBoundingClientRect();
        dragRef.current = {
            dragging: true,
            startX: event.clientX,
            startY: event.clientY,
            originLeft: rect.left,
            originTop: rect.top,
        };
        setPanelPos({ left: rect.left, top: rect.top });
        document.body.style.userSelect = 'none';
    }, []);
    useEffect(() => {
        if (!visible)
            return;
        const onMouseMove = (event) => {
            if (!dragRef.current.dragging || !panelRef.current || !headerRef.current)
                return;
            const phone = panelRef.current;
            const header = headerRef.current;
            const { startX, startY, originLeft, originTop } = dragRef.current;
            const nextLeft = originLeft + event.clientX - startX;
            const nextTop = originTop + event.clientY - startY;
            const maxLeft = Math.max(0, window.innerWidth - phone.offsetWidth);
            const maxTop = Math.max(0, window.innerHeight - header.offsetHeight);
            setPanelPos({
                left: Math.max(0, Math.min(nextLeft, maxLeft)),
                top: Math.max(0, Math.min(nextTop, maxTop)),
            });
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', endDrag);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', endDrag);
            document.body.style.userSelect = '';
        };
    }, [visible, endDrag]);
    const panelStyle = panelPos != null
        ? {
            left: panelPos.left,
            top: panelPos.top,
            right: 'auto',
            bottom: 'auto',
        }
        : {};
    const sipHint = sipRegistrationHint(sipStatus, sipError);
    // const sipExtensionLine = isSipAccountConfigured(account)
    //     ? `Ext ${account.extension} @ ${account.sipDomainHost}`
    //     : 'Extension not assigned';
    const hasActiveOutboundOrTalking = conversationUi?.sipState === SessionState.Establishing ||
        conversationUi?.sipState === SessionState.Initial ||
        conversationUi?.sipState === SessionState.Established ||
        conversationUi?.sipState === SessionState.Terminating;
    const showHangUp = Boolean(conversationUi && hasActiveOutboundOrTalking);
    const canAttemptDial = isSipAccountConfigured(account) &&
        Boolean(enteredNumber.trim()) &&
        !showHangUp &&
        sipReadyForOutboundDial(sipStatus);
    const timerLine = useMemo(() => {
        if (!conversationUi)
            return null;
        const now = Date.now();
        const { sipState, callStartedMs, answeredAtMs } = conversationUi;
        if (sipState === SessionState.Established && answeredAtMs != null) {
            const secs = Math.floor(Math.max(0, (now - answeredAtMs) / 1000));
            return formatMmSs(secs);
        }
        if (sipState === SessionState.Initial ||
            sipState === SessionState.Establishing ||
            sipState === SessionState.Terminating) {
            const secs = Math.floor(Math.max(0, (now - callStartedMs) / 1000));
            return formatMmSs(secs);
        }
        return null;
    }, [conversationUi, timerTick]);
    const handleDial = useCallback(async () => {
        setDialFeedback(null);
        try {
            await dial(enteredNumber);
            onDial?.(enteredNumber);
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            setDialFeedback(msg);
        }
    }, [dial, enteredNumber, onDial]);
    const handleHangup = useCallback(() => {
        playHangupTone();
        void hangupActiveOrDeclineIncoming();
    }, [hangupActiveOrDeclineIncoming, playHangupTone]);
    if (!visible)
        return null;
    const outboundRingingVisual = conversationUi?.direction === 'outbound' &&
        conversationUi.outboundPhase !== 'line-busy' &&
        (conversationUi.sipState === SessionState.Establishing ||
            conversationUi.sipState === SessionState.Initial);
    /** Portal keeps stacking above layout chrome (sidebar/topbar use z-index ~997–999); toolbar is only 996. */
    return createPortal(_jsxs("section", { ref: panelRef, className: `softphone-modal${outboundRingingVisual ? ' softphone-modal--ringing' : ''}`, style: panelStyle, role: "dialog", "aria-label": "Softphone dialer", "aria-modal": false, children: [_jsxs("header", { ref: headerRef, className: "softphone-header", onMouseDown: onHeaderMouseDown, children: [_jsxs("div", { className: "softphone-header__brand", children: [_jsx("span", { className: "softphone-header__icon", "aria-hidden": true, children: _jsx("svg", { viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 5a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.45.57 3.57a1 1 0 01-.25 1.01l-2.2 2.21z", stroke: "currentColor", strokeWidth: "1.75", strokeLinecap: "round", strokeLinejoin: "round" }) }) }), _jsxs("div", { className: "softphone-header__text", children: [_jsx("span", { className: "softphone-header__title", children: "Softphone" }), _jsx("span", { className: "softphone-header__drag-hint", children: "Drag to move" })] })] }), _jsx("button", { type: "button", className: "softphone-header__close", "aria-label": "Close softphone", onClick: onHide, children: _jsx("svg", { viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: _jsx("path", { d: "M6 6l12 12M18 6L6 18", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }) }) })] }), _jsxs("div", { className: "softphone-body", children: [_jsxs("div", { className: "softphone-sip-meta", children: [isOnBreak ? (_jsx("p", { className: "softphone-break-hint", children: "On break \u2014 outbound calls only" })) : null, _jsxs("div", { className: `softphone-sip-badge softphone-sip-badge--${sipHint.tone}`, title: isSipAccountConfigured(account)
                                    ? `WebSocket ${account.sipWebSocketServerUrl}`
                                    : 'No softphone extension assigned in your profile', children: [_jsx("span", { className: "softphone-sip-badge__dot", "aria-hidden": true }), _jsx("span", { className: "softphone-sip-badge__label", children: sipHint.line })] })] }), conversationUi ? (_jsxs("div", { className: `softphone-call-state softphone-call-state${sipCallPhaseTone(conversationUi)}`, role: "status", "aria-live": "polite", children: [_jsxs("div", { className: "softphone-call-state__phase", children: [_jsx("span", { children: sipCallPhaseLabel(conversationUi) }), timerLine ? (_jsx("span", { className: "softphone-call-state__timer", children: timerLine })) : null] }), _jsx("div", { className: "softphone-call-state__remote", children: conversationUi.remoteLabel })] })) : null, isOnBackendAcw ? (_jsxs("div", { className: "softphone-acw-status", role: "status", "aria-live": "polite", children: [_jsxs("div", { className: "softphone-acw-status__head", children: [_jsx("span", { className: "softphone-acw-status__badge", children: "ACW" }), _jsx("span", { className: "softphone-acw-status__elapsed", children: formatMmSs(acwElapsedSeconds) })] }), _jsx("p", { className: "softphone-acw-status__detail m-0", children: acwRemainingSeconds > 0
                                    ? `${formatMmSs(acwRemainingSeconds)} until ready for calls`
                                    : 'After-call work — not receiving queue calls' })] })) : null, _jsxs("div", { className: `softphone-display${enteredNumber ? ' softphone-display--filled' : ''}`, children: [_jsx("input", { className: "softphone-display__input", type: "tel", inputMode: "tel", autoComplete: "off", spellCheck: false, placeholder: "Enter number", readOnly: true, value: enteredNumber, "aria-label": "Entered phone number or extension", onPaste: (e) => {
                                    if (dialpadLocked)
                                        return;
                                    e.preventDefault();
                                    const text = e.clipboardData.getData('text');
                                    setEnteredNumber((prev) => sanitizeDialInput(prev + text));
                                } }), enteredNumber && !dialpadLocked ? (_jsx("button", { type: "button", className: "softphone-display__backspace", "aria-label": "Delete last digit", onClick: removeLast, children: _jsxs("svg", { width: "15", height: "15", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, children: [_jsx("path", { d: "M9.2 5.5H19a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9.2L3 12l6.2-6.5Z", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M13.5 9.5l4 4m0-4-4 4", stroke: "currentColor", strokeWidth: "2.2", strokeLinecap: "round" })] }) })) : null] }), _jsx(SoftphoneDialpad, { onDigit: appendDigit, onClear: clearNumber, onBackspace: removeLast, disabled: dialpadLocked, mode: dialpadMode }), dialFeedback ? (_jsx("p", { className: "softphone-dial-feedback", role: "alert", children: dialFeedback })) : null, _jsx("div", { className: "softphone-primary-action", children: showHangUp ? (_jsxs("button", { type: "button", className: "softphone-call-btn softphone-call-btn--hangup", "aria-label": "Hang up", title: "Hang up", onClick: handleHangup, children: [_jsx("svg", { viewBox: "0 0 1024 1024", "aria-hidden": true, children: _jsx("path", { d: HANGUP_ICON_PATH }) }), _jsx("span", { children: "End call" })] })) : (_jsxs("button", { type: "button", className: "softphone-call-btn softphone-call-btn--dial", "aria-label": "Dial number", disabled: !canAttemptDial, onClick: () => {
                                void handleDial();
                            }, children: [_jsx("svg", { viewBox: "0 0 1024 1024", "aria-hidden": true, children: _jsx("path", { d: PHONE_ICON_PATH }) }), _jsx("span", { children: "Call" })] })) })] })] }), document.body);
}
